import graphql from 'api/graphql'

import { doHideModal } from 'actions/modals'
import { doOpenTicketPage, doOpenForwardPage } from 'actions/pages'

import { selectDeviceTypeIsMobile } from 'selectors/app/base'
import { oauthTokenSelector } from 'selectors/app'
import { selectIsForwarding } from 'selectors/page'

import { doUpdateDraft, selectTicketDraft } from 'ducks/drafts'
import { selectInterpolateCannedReply } from 'ducks/cannedReplies'
import { doGraphqlRequest } from 'ducks/requests'

import { runOnNextTick } from 'util/functions'
import { v4 as uuidV4 } from 'uuid'
import { getWrappedRootNodes } from 'util/cannedReply'
import editor from 'shared/editor/utils'

import {
  FETCH_CANNED_REPLY_CATEGORIES,
  FETCH_CANNED_REPLIES,
  TOGGLE_CANNED_REPLY_COLLAPSED_STATE,
  INSERT_CANNED_REPLY_REQUEST,
  INSERT_CANNED_REPLY,
  SET_TEMPLATE_SEARCH_TERM,
  CLEAR_TEMPLATES,
} from './types'
import {
  selectCannedReplyCategories,
  selectIsLoadingCategoriesOrTemplates,
  selectCurrentCategoryBatchId,
  selectLoadingCannedReplyCategoryIds,
  selectLoadedCannedReplyCategoryIds,
  selectLastRequestedSearchTerm,
} from './selectors'

export const BASIC_TEMPLATE_FIELDS = `
  id
  name
  categoryId
  snippet
  subject
`

export const doFetchCannedReplyCategories = (
  keyword,
  options = {}
) => dispatch => {
  const query = `
      query CommentTemplateCategoriesQuery(
        $keyword: String
      ) {
        commentTemplateCategories(keyword: $keyword) {
          id
          name
          total
        }
      }
    `
  return dispatch(
    doGraphqlRequest(FETCH_CANNED_REPLY_CATEGORIES, query, { keyword }, options)
  )
}

export const doFetchCannedReplies = (categoryIds, keyword = '', source) => (
  dispatch,
  getState
) => {
  const state = getState()
  const categoryBatchId = selectCurrentCategoryBatchId(state)
  const lastRequestSearchTerm = selectLastRequestedSearchTerm(state)
  const loadingCannedReplyCategoryIds = selectLoadingCannedReplyCategoryIds(
    state
  )
  const loadedCannedReplyCategoryIds = selectLoadedCannedReplyCategoryIds(state)

  const isEmptySearch = categoryIds.length === 0 && keyword.trim() === ''
  const isKeywordChange = lastRequestSearchTerm !== keyword

  let pendingCategoryIds = categoryIds

  if (lastRequestSearchTerm === keyword) {
    pendingCategoryIds = categoryIds.filter(
      c => !loadingCannedReplyCategoryIds.includes(c)
    )
    pendingCategoryIds = categoryIds.filter(
      c => !loadedCannedReplyCategoryIds.includes(c)
    )
  }
  const isMissingCategoryTemplates = pendingCategoryIds.length > 0

  if (!isEmptySearch && !isKeywordChange && !isMissingCategoryTemplates)
    return Promise.resolve()

  const query = `
      query CommentTemplateQuery(
        $categoryIds: [String]
        $keyword: String
        $source: String
      ) {
        commentTemplates(categoryIds: $categoryIds, keyword: $keyword, source: $source) {
          ${BASIC_TEMPLATE_FIELDS}
        }
      }
    `

  return dispatch(
    doGraphqlRequest(
      FETCH_CANNED_REPLIES,
      query,
      {
        categoryIds: pendingCategoryIds,
        keyword,
        source: source || 'es',
      },
      {
        meta: {
          categoryBatchId,
        },
      }
    )
  )
}

export function doToggleCollapedStateForCategories(categoryIds) {
  return {
    type: TOGGLE_CANNED_REPLY_COLLAPSED_STATE,
    payload: {
      categoryIds,
    },
  }
}

export const doFetchCannedReplyCategoriesAndTemplatesBySearch = keyword => (
  dispatch,
  getState
) => {
  const stateKeyword = selectLastRequestedSearchTerm(getState())
  const requestId = uuidV4()
  if (stateKeyword !== keyword) {
    return doFetchCannedReplyCategories(keyword, { requestId })(
      dispatch,
      getState
    ).then(() => {
      const state = getState()
      const categoryBatchId = selectCurrentCategoryBatchId(state)
      if (keyword.trim() !== '' && requestId === categoryBatchId) {
        const categories = selectCannedReplyCategories(getState())
        const categoryIds = categories.map(c => c.id)
        dispatch(doToggleCollapedStateForCategories(categoryIds))
        return doFetchCannedReplies(categoryIds, keyword)(dispatch, getState)
      }
      return Promise.resolve()
    })
  }
  return Promise.resolve()
}

// we only allow one search globally
let searchTimeoutId
const searchDelay = 125

export const doFetchCannedReplyTemplatesBySearch = keyword => (
  dispatch,
  getState
) => {
  dispatch({ type: SET_TEMPLATE_SEARCH_TERM, payload: { keyword } })
  const state = getState()
  const isLoading = selectIsLoadingCategoriesOrTemplates(state)
  if (keyword.trim() !== '' && !isLoading) {
    if (searchTimeoutId) clearTimeout(searchTimeoutId)
    searchTimeoutId = setTimeout(() => {
      dispatch(doClearTemplates())
      doFetchCannedReplies([], keyword)(dispatch, getState)
    }, searchDelay)
    return Promise.resolve()
  }
  return Promise.resolve()
}

export function doSelectReply(ticketId, reply) {
  return (dispatch, getState) => {
    if (!reply) return false
    const state = getState()
    const isOnMobile = selectDeviceTypeIsMobile(state)
    const isForwarding = selectIsForwarding(state)
    if (ticketId !== 'new' && isOnMobile) {
      // TODO (jscheel): We probably need a selector in selectors/pageActions.js
      // that just returns exactly what is already open, but this will do for now.
      if (isForwarding) {
        dispatch(doOpenTicketPage(ticketId))
      } else {
        dispatch(doOpenForwardPage(ticketId))
      }
    }
    runOnNextTick(() => {
      dispatch(doInsertCannedReply(reply))
      dispatch(doHideModal())
    })
    return true
  }
}

const fetchCannedReplyBody = (token, reply) => {
  const query = `
    query CannedRepliesBodyQuery {
      commentTemplate(id: "${reply.id}") {
        body
      }
    }
  `
  return graphql(token, query).then(response => {
    const data = response.json.data
    if (data.commentTemplate) {
      return data.commentTemplate.body
    }
    return ''
  })
}

let previousBodyContent
export function doInsertCannedReply(reply) {
  return (dispatch, getState) => {
    const state = getState()
    const token = oauthTokenSelector(state)
    const draft = selectTicketDraft(state)
    const interpolateCannedReply = selectInterpolateCannedReply(state)

    dispatch({
      type: INSERT_CANNED_REPLY_REQUEST,
      data: { reply },
    })

    return fetchCannedReplyBody(token, reply).then(cannedReplyBody => {
      const body = draft.body || ''
      let newTitle = draft.title || ''
      let newBody = body
      previousBodyContent = body
      const cannedReply = interpolateCannedReply(cannedReplyBody)

      draft.templateId = reply && reply.id

      // Jank Level: 5.
      // Interpolate the subject and save the draft. If we dont do this , the
      // subject gets cleared when the below editor.insertContent call fires a
      // 'change' event, our onChange handler fires, the draft gets updated,
      // (but the subject in the current Editor hasnt been updated yet).
      // Ideally we'd suppress any 'change' event from firing instead, but
      // TinyMCEs doco doesn't help much...
      if (draft.id === 'new' && reply.subject) {
        newTitle = interpolateCannedReply(reply.subject)
        dispatch(
          doUpdateDraft({
            ...draft,
            title: newTitle,
          })
        )
      }

      const tinyEditor = editor.available()
      if (tinyEditor) {
        const wrappedNodes = getWrappedRootNodes(cannedReply, tinyEditor)
        // Images in the settings editor are styled using the style property
        // For security, we remove that property
        // Which is why we need to copy width and height to their own properties
        const images = Array.from(wrappedNodes.getElementsByTagName('img'))
        images.forEach(image => {
          if (!image.style) return
          const style = image.style
          // eslint-disable-next-line no-param-reassign
          if (style.height) image.height = parseInt(style.height, 10)
          // eslint-disable-next-line no-param-reassign
          if (style.width) image.width = parseInt(style.width, 10)
        })
        tinyEditor.execCommand(
          'mceInsertContent',
          false,
          wrappedNodes.innerHTML
        )

        // now that we've inserted, we need to set the new body
        const updatedBody = editor.getContent()
        newBody = updatedBody

        dispatch(
          // NOTE (jscheel): We have to make sure we do not mutate the draft
          // object, as it comes out of a memoized decorator. We used to do
          // `draft.body = 'foo'`, which destructively-mutated a memoized copy
          // of the draft. Not good!
          doUpdateDraft({
            ...draft,
            title: newTitle,
            body: newBody,
          })
        )
      }

      return dispatch({
        type: INSERT_CANNED_REPLY,
        data: {
          draft: {
            ...draft,
            title: newTitle,
            body: newBody,
          },
          reply,
        },
      })
    })
  }
}

export function doSetTemplateSearchTerm(keyword) {
  return {
    type: SET_TEMPLATE_SEARCH_TERM,
    payload: {
      keyword,
    },
  }
}

export function doClearTemplates() {
  return {
    type: CLEAR_TEMPLATES,
  }
}

export const doUndoInsertCannedReply = () => () => {
  editor.setContent(previousBodyContent, { focus: true })
}
